import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AIAdvisoryBadge } from "@/components/owner/AIAdvisoryBadge";
import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DESTINATION_LABELS,
  scanListingPhotos,
  type ListingItemDestination,
  type ListingScanDetectedItem,
  type ListingScanPhoto,
} from "@/lib/listingPhotoScan";

type Phase = "scanning" | "review" | "error";

interface Step2bListingScanProps {
  propertyId: string;
  photos: ListingScanPhoto[];
  items: ListingScanDetectedItem[];
  onItemsChange: (items: ListingScanDetectedItem[]) => void;
  onReviewReady?: (ready: boolean) => void;
}

const DESTINATIONS: ListingItemDestination[] = [
  "cleaning_task",
  "unit_check",
  "supply_closet",
  "guest_amenities",
  "maintenance",
  "skip",
];

export function Step2bListingScan({ propertyId, photos, items, onItemsChange, onReviewReady }: Step2bListingScanProps) {
  const [phase, setPhase] = useState<Phase>("scanning");
  const [progress, setProgress] = useState({ done: 0, total: 0, room: "" });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showLowConfidence, setShowLowConfidence] = useState(false);
  const scanStarted = useRef(false);

  const runScan = useCallback(async () => {
    if (photos.length === 0) {
      setPhase("review");
      onItemsChange([]);
      onReviewReady?.(true);
      return;
    }
    setPhase("scanning");
    setErrorMessage(null);
    try {
      const { items: detected } = await scanListingPhotos(propertyId, photos, (done, total, roomName) => {
        setProgress({ done, total, room: roomName });
      });
      onItemsChange(detected);
      setPhase("review");
      onReviewReady?.(true);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : "Scan failed");
      setPhase("error");
      onReviewReady?.(false);
    }
  }, [propertyId, photos, onReviewReady]);

  useEffect(() => {
    if (scanStarted.current) return;
    scanStarted.current = true;
    void runScan();
  }, [runScan]);

  const LOW_CONF = 0.75;
  const displayed = showLowConfidence
    ? items
    : items.filter((i) => i.confidence >= LOW_CONF);

  const updateItem = (id: string, patch: Partial<ListingScanDetectedItem>) => {
    onItemsChange(items.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const includedCount = items.filter((i) => i.included && i.destination !== "skip").length;

  if (phase === "scanning") {
    const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <div className="space-y-6 py-8 text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
        <div>
          <h2 className="text-xl font-semibold">Scanning listing photos</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            AI is identifying supplies and fixtures in each room photo. This usually takes under a minute.
          </p>
          {progress.total > 0 ? (
            <p className="text-xs text-muted-foreground mt-3">
              Room {progress.done} of {progress.total}
              {progress.room ? ` — ${progress.room}` : ""} ({pct}%)
            </p>
          ) : null}
        </div>
        <AIAdvisoryBadge className="mx-auto" />
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="space-y-4 py-6">
        <div className="flex gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-destructive">Could not complete scan</p>
            <p className="text-sm text-muted-foreground mt-1">{errorMessage}</p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={() => { scanStarted.current = false; void runScan(); }}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Scan for supplies &amp; fixtures</h1>
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          AI identified what's in each room. Review, adjust the category if needed, then save.
          Nothing is written until you tap <span className="text-foreground font-medium">Save &amp; continue</span>.
        </p>
      </div>

      <AIAdvisoryBadge />

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground rounded-lg border p-4 bg-muted/30">
          No items detected. You can continue and add inventory manually later, or go back and assign more photos to
          rooms.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{includedCount}</span> selected to save
            </span>
            <div className="flex items-center gap-2">
              <Checkbox
                id="low-conf"
                checked={showLowConfidence}
                onCheckedChange={(c) => setShowLowConfidence(!!c)}
              />
              <Label htmlFor="low-conf" className="text-xs font-normal cursor-pointer">
                Show low-confidence
              </Label>
            </div>
          </div>

          <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-1">
            {displayed.map((item) => (
              <div
                key={item.id}
                className={cn(
                  "rounded-lg border p-3 space-y-2",
                  !item.included && "opacity-60 bg-muted/40"
                )}
              >
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={item.included}
                    onCheckedChange={(c) => updateItem(item.id, { included: !!c })}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap gap-2 items-center">
                      <Input
                        className="h-8 text-sm flex-1 min-w-[140px]"
                        value={item.name}
                        onChange={(e) => updateItem(item.id, { name: e.target.value })}
                      />
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                        {Math.round(item.confidence * 100)}%
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Room</Label>
                        <Input
                          className="h-8 text-xs"
                          value={item.roomName}
                          onChange={(e) => updateItem(item.id, { roomName: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Save as</Label>
                        <Select
                          value={item.destination}
                          onValueChange={(v) =>
                            updateItem(item.id, { destination: v as ListingItemDestination })
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DESTINATIONS.map((d) => (
                              <SelectItem key={d} value={d} className="text-xs">
                                {DESTINATION_LABELS[d]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {item.destination === "cleaning_task" && item.suggestedTask ? (
                      <p className="text-[10px] text-primary leading-snug font-medium">
                        → {item.suggestedTask}
                      </p>
                    ) : item.evidence ? (
                      <p className="text-[10px] text-muted-foreground leading-snug">{item.evidence}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Items ready for parent save handler. */
export function getIncludedListingScanItems(items: ListingScanDetectedItem[]) {
  return items.filter((i) => i.included && i.name.trim() && i.destination !== "skip");
}
